// Comprehensive error handling and recovery utilities

import Swal from 'sweetalert2';

/**
 * Error types for categorization
 */
export const ERROR_TYPES = {
  NETWORK: 'network',
  VALIDATION: 'validation',
  AUTHENTICATION: 'authentication',
  PERMISSION: 'permission',
  PROCESSING: 'processing',
  UNKNOWN: 'unknown',
  USER_INPUT: 'user_input',
  SYSTEM: 'system',
  API: 'api'
};

/**
 * Error severity levels
 */
export const ERROR_SEVERITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical'
};

/**
 * Custom error class for better error handling
 */
export class EnhancedError extends Error {
  constructor(message, type = ERROR_TYPES.UNKNOWN, severity = ERROR_SEVERITY.MEDIUM, context = {}) {
    super(message);
    this.name = 'EnhancedError';
    this.type = type;
    this.severity = severity;
    this.context = context;
    this.timestamp = new Date();
    this.stack = (new Error()).stack;
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      type: this.type,
      severity: this.severity,
      context: this.context,
      timestamp: this.timestamp,
      stack: this.stack
    };
  }
}

/**
 * Error boundary utility for React components
 */
export class ErrorBoundaryHandler {
  constructor() {
    this.errors = [];
    this.listeners = [];
  }

  /**
   * Handle caught errors
   * @param {Error} error - The error that occurred
   * @param {Object} errorInfo - Additional error information
   * @param {Object} context - Context where error occurred
   */
  handleError(error, errorInfo = {}, context = {}) {
    const enhancedError = this.enhanceError(error, context);
    this.logError(enhancedError, errorInfo);
    this.notifyListeners(enhancedError, errorInfo);
    
    // Determine recovery strategy
    const recovery = this.determineRecoveryStrategy(enhancedError);
    return recovery;
  }

  /**
   * Enhance basic error with additional context
   * @param {Error} error - Original error
   * @param {Object} context - Additional context
   * @returns {EnhancedError} Enhanced error object
   */
  enhanceError(error, context = {}) {
    if (error instanceof EnhancedError) {
      return error;
    }

    let type = ERROR_TYPES.UNKNOWN;
    let severity = ERROR_SEVERITY.MEDIUM;

    // Categorize error based on message and stack
    if (error.name === 'TypeError' || error.name === 'ReferenceError') {
      type = ERROR_TYPES.SYSTEM;
      severity = ERROR_SEVERITY.HIGH;
    } else if (error.name === 'NetworkError' || error.message.includes('fetch')) {
      type = ERROR_TYPES.NETWORK;
      severity = ERROR_SEVERITY.MEDIUM;
    } else if (error.name === 'ValidationError') {
      type = ERROR_TYPES.VALIDATION;
      severity = ERROR_SEVERITY.LOW;
    } else if (error.status === 401 || error.status === 403) {
      type = ERROR_TYPES.AUTHENTICATION;
      severity = ERROR_SEVERITY.HIGH;
    } else if (error.status >= 400 && error.status < 500) {
      type = ERROR_TYPES.API;
      severity = ERROR_SEVERITY.MEDIUM;
    } else if (error.status >= 500) {
      type = ERROR_TYPES.SYSTEM;
      severity = ERROR_SEVERITY.HIGH;
    }

    return new EnhancedError(error.message, type, severity, {
      originalError: error,
      ...context
    });
  }

  /**
   * Log error with appropriate level
   * @param {EnhancedError} error - Enhanced error object
   * @param {Object} errorInfo - Additional error information
   */
  logError(error, errorInfo = {}) {
    const logEntry = {
      ...error.toJSON(),
      errorInfo,
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: this.getCurrentUserId(),
      sessionId: this.getSessionId()
    };

    // Store in local storage for offline recovery
    this.storeErrorLocally(logEntry);

    // Console logging with appropriate level
    switch (error.severity) {
      case ERROR_SEVERITY.LOW:
        console.info('Low severity error:', logEntry);
        break;
      case ERROR_SEVERITY.MEDIUM:
        console.warn('Medium severity error:', logEntry);
        break;
      case ERROR_SEVERITY.HIGH:
        console.error('High severity error:', logEntry);
        break;
      case ERROR_SEVERITY.CRITICAL:
        console.error('CRITICAL ERROR:', logEntry);
        break;
      default:
        console.log('Error:', logEntry);
    }

    // Send to error tracking service if available
    this.sendToErrorService(logEntry);
  }

  /**
   * Determine recovery strategy based on error type
   * @param {EnhancedError} error - Enhanced error object
   * @returns {Object} Recovery strategy
   */
  determineRecoveryStrategy(error) {
    const strategies = {
      [ERROR_TYPES.NETWORK]: {
        autoRetry: true,
        maxRetries: 3,
        retryDelay: 2000,
        fallback: () => this.showNetworkErrorDialog(),
        offlineSupport: true
      },
      [ERROR_TYPES.VALIDATION]: {
        autoRetry: false,
        showFieldErrors: true,
        focusFirstError: true,
        fallback: () => this.showValidationErrorDialog(error)
      },
      [ERROR_TYPES.AUTHENTICATION]: {
        autoRetry: false,
        redirectToLogin: true,
        clearSession: true,
        fallback: () => this.showAuthErrorDialog()
      },
      [ERROR_TYPES.API]: {
        autoRetry: true,
        maxRetries: 2,
        retryDelay: 1000,
        fallback: () => this.showApiErrorDialog(error)
      },
      [ERROR_TYPES.SYSTEM]: {
        autoRetry: false,
        reloadPage: error.severity === ERROR_SEVERITY.CRITICAL,
        fallback: () => this.showSystemErrorDialog(error)
      },
      [ERROR_TYPES.PROCESSING]: {
        autoRetry: true,
        maxRetries: 1,
        fallback: () => this.showProcessingErrorDialog(error)
      }
    };

    return strategies[error.type] || strategies[ERROR_TYPES.UNKNOWN] || {
      autoRetry: false,
      fallback: () => this.showGenericErrorDialog(error)
    };
  }

  /**
   * Execute recovery strategy
   * @param {Function} operation - Operation to retry
   * @param {Object} strategy - Recovery strategy
   * @returns {Promise} Recovery promise
   */
  async executeRecovery(operation, strategy) {
    if (strategy.autoRetry) {
      return this.retryOperation(operation, strategy.maxRetries || 3, strategy.retryDelay || 1000);
    }

    if (strategy.reloadPage) {
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    }

    if (strategy.redirectToLogin) {
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
    }

    if (strategy.fallback) {
      return strategy.fallback();
    }
  }

  /**
   * Retry operation with exponential backoff
   * @param {Function} operation - Operation to retry
   * @param {number} maxRetries - Maximum retry attempts
   * @param {number} delay - Base delay in milliseconds
   * @returns {Promise} Operation promise
   */
  async retryOperation(operation, maxRetries = 3, delay = 1000) {
    for (let i = 0; i < maxRetries; i++) {
      try {
        return await operation();
      } catch (error) {
        if (i === maxRetries - 1) {
          throw error;
        }

        const backoffDelay = delay * Math.pow(2, i);
        await new Promise(resolve => setTimeout(resolve, backoffDelay));
      }
    }
  }

  /**
   * Store error locally for offline recovery
   * @param {Object} errorEntry - Error entry to store
   */
  storeErrorLocally(errorEntry) {
    try {
      const errors = JSON.parse(localStorage.getItem('errorLog') || '[]');
      errors.push(errorEntry);
      
      // Keep only last 50 errors
      if (errors.length > 50) {
        errors.splice(0, errors.length - 50);
      }
      
      localStorage.setItem('errorLog', JSON.stringify(errors));
    } catch (e) {
      console.warn('Failed to store error locally:', e);
    }
  }

  /**
   * Send error to external error tracking service
   * @param {Object} errorEntry - Error entry to send
   */
  async sendToErrorService(errorEntry) {
    try {
      // Only send high severity errors to avoid spam
      if ([ERROR_SEVERITY.HIGH, ERROR_SEVERITY.CRITICAL].includes(errorEntry.severity)) {
        // Replace with your error tracking service endpoint
        // await fetch('/api/errors', {
        //   method: 'POST',
        //   headers: { 'Content-Type': 'application/json' },
        //   body: JSON.stringify(errorEntry)
        // });
      }
    } catch (e) {
      console.warn('Failed to send error to tracking service:', e);
    }
  }

  /**
   * Show user-friendly error dialogs
   */
  showNetworkErrorDialog() {
    return Swal.fire({
      title: 'Connection Problem',
      text: 'Unable to connect to the server. Please check your internet connection and try again.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Retry',
      cancelButtonText: 'Continue Offline'
    });
  }

  showValidationErrorDialog(error) {
    return Swal.fire({
      title: 'Validation Error',
      text: error.message || 'Please check your input and try again.',
      icon: 'error',
      confirmButtonText: 'OK'
    });
  }

  showAuthErrorDialog() {
    return Swal.fire({
      title: 'Authentication Required',
      text: 'Your session has expired. Please log in again.',
      icon: 'warning',
      confirmButtonText: 'Log In',
      allowOutsideClick: false
    });
  }

  showApiErrorDialog(error) {
    return Swal.fire({
      title: 'Service Unavailable',
      text: 'The service is temporarily unavailable. Please try again in a few moments.',
      icon: 'error',
      showCancelButton: true,
      confirmButtonText: 'Retry',
      cancelButtonText: 'Cancel'
    });
  }

  showSystemErrorDialog(error) {
    return Swal.fire({
      title: 'System Error',
      text: 'A system error occurred. The page will reload automatically.',
      icon: 'error',
      confirmButtonText: 'Reload Now',
      timer: 5000,
      timerProgressBar: true
    });
  }

  showProcessingErrorDialog(error) {
    return Swal.fire({
      title: 'Processing Error',
      text: 'Unable to process your request. Please try again.',
      icon: 'error',
      confirmButtonText: 'Try Again'
    });
  }

  showGenericErrorDialog(error) {
    return Swal.fire({
      title: 'Unexpected Error',
      text: 'An unexpected error occurred. Please refresh the page and try again.',
      icon: 'error',
      confirmButtonText: 'Refresh Page'
    });
  }

  /**
   * Utility functions
   */
  getCurrentUserId() {
    // Implement based on your authentication system
    return localStorage.getItem('userId') || 'anonymous';
  }

  getSessionId() {
    let sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      sessionId = 'session-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
      sessionStorage.setItem('sessionId', sessionId);
    }
    return sessionId;
  }

  /**
   * Add error listener
   * @param {Function} listener - Error listener function
   */
  addListener(listener) {
    this.listeners.push(listener);
  }

  /**
   * Remove error listener
   * @param {Function} listener - Error listener function
   */
  removeListener(listener) {
    this.listeners = this.listeners.filter(l => l !== listener);
  }

  /**
   * Notify all listeners about error
   * @param {EnhancedError} error - Error object
   * @param {Object} errorInfo - Additional error info
   */
  notifyListeners(error, errorInfo) {
    this.listeners.forEach(listener => {
      try {
        listener(error, errorInfo);
      } catch (e) {
        console.warn('Error in error listener:', e);
      }
    });
  }

  /**
   * Get stored errors from local storage
   * @returns {Array} Array of stored errors
   */
  getStoredErrors() {
    try {
      return JSON.parse(localStorage.getItem('errorLog') || '[]');
    } catch (e) {
      return [];
    }
  }

  /**
   * Clear stored errors
   */
  clearStoredErrors() {
    try {
      localStorage.removeItem('errorLog');
    } catch (e) {
      console.warn('Failed to clear error log:', e);
    }
  }

  /**
   * Get error statistics
   * @returns {Object} Error statistics
   */
  getErrorStats() {
    const errors = this.getStoredErrors();
    const stats = {
      total: errors.length,
      byType: {},
      bySeverity: {},
      recent: errors.filter(e => Date.now() - new Date(e.timestamp).getTime() < 24 * 60 * 60 * 1000)
    };

    errors.forEach(error => {
      stats.byType[error.type] = (stats.byType[error.type] || 0) + 1;
      stats.bySeverity[error.severity] = (stats.bySeverity[error.severity] || 0) + 1;
    });

    return stats;
  }
}

/**
 * Global error handler instance
 */
export const globalErrorHandler = new ErrorBoundaryHandler();

/**
 * React error boundary hook
 * @param {Function} onError - Error handler function
 * @returns {Function} Error handler
 */
export const useErrorBoundary = (onError = null) => {
  const handleError = (error, errorInfo) => {
    const recovery = globalErrorHandler.handleError(error, errorInfo);
    
    if (onError) {
      onError(error, errorInfo, recovery);
    }
    
    return recovery;
  };

  return handleError;
};

/**
 * Async error wrapper
 * @param {Function} asyncFn - Async function to wrap
 * @param {Object} context - Error context
 * @returns {Function} Wrapped function
 */
export const withErrorHandling = (asyncFn, context = {}) => {
  return async (...args) => {
    try {
      return await asyncFn(...args);
    } catch (error) {
      const recovery = globalErrorHandler.handleError(error, {}, context);
      
      if (recovery.autoRetry) {
        return globalErrorHandler.executeRecovery(() => asyncFn(...args), recovery);
      }
      
      throw error;
    }
  };
};

/**
 * Form validation error handler
 * @param {Object} errors - Validation errors
 * @param {HTMLFormElement} form - Form element
 */
export const handleValidationErrors = (errors, form) => {
  Object.keys(errors).forEach(fieldName => {
    const field = form.querySelector(`[name="${fieldName}"]`);
    if (field) {
      const error = new EnhancedError(errors[fieldName], ERROR_TYPES.VALIDATION, ERROR_SEVERITY.LOW, {
        field: fieldName,
        value: field.value
      });
      
      globalErrorHandler.handleError(error);
      
      // Add visual feedback
      field.classList.add('error');
      field.setAttribute('aria-invalid', 'true');
      
      // Show error message
      let errorElement = field.parentNode.querySelector('.error-message');
      if (!errorElement) {
        errorElement = document.createElement('div');
        errorElement.className = 'error-message';
        errorElement.setAttribute('role', 'alert');
        field.parentNode.appendChild(errorElement);
      }
      errorElement.textContent = errors[fieldName];
    }
  });
  
  // Focus first error field
  const firstErrorField = form.querySelector('.error');
  if (firstErrorField) {
    firstErrorField.focus();
  }
};

/**
 * Initialize global error handling
 */
export const initializeErrorHandling = () => {
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const error = new EnhancedError(
      event.reason?.message || 'Unhandled promise rejection',
      ERROR_TYPES.SYSTEM,
      ERROR_SEVERITY.HIGH,
      { reason: event.reason }
    );
    
    globalErrorHandler.handleError(error);
  });

  // Handle global JavaScript errors
  window.addEventListener('error', (event) => {
    const error = new EnhancedError(
      event.error?.message || event.message || 'Global JavaScript error',
      ERROR_TYPES.SYSTEM,
      ERROR_SEVERITY.HIGH,
      {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error
      }
    );
    
    globalErrorHandler.handleError(error);
  });

  // Handle network errors
  window.addEventListener('offline', () => {
    const error = new EnhancedError(
      'Network connection lost',
      ERROR_TYPES.NETWORK,
      ERROR_SEVERITY.MEDIUM,
      { online: false }
    );
    
    globalErrorHandler.handleError(error);
  });

  window.addEventListener('online', () => {
    console.log('Network connection restored');
    // Attempt to sync any offline errors
    globalErrorHandler.sendOfflineErrors();
  });
};

// Add method to send offline errors when connection is restored
globalErrorHandler.sendOfflineErrors = async function() {
  const errors = this.getStoredErrors();
  const unsentErrors = errors.filter(error => !error.sent);
  
  for (const error of unsentErrors) {
    try {
      await this.sendToErrorService(error);
      error.sent = true;
    } catch (e) {
      console.warn('Failed to send offline error:', e);
      break;
    }
  }
  
  // Update stored errors
  try {
    localStorage.setItem('errorLog', JSON.stringify(errors));
  } catch (e) {
    console.warn('Failed to update error log:', e);
  }
};

// Initialize error handling when module is loaded
if (typeof window !== 'undefined') {
  initializeErrorHandling();
}

const errorHandlingUtils = {
  EnhancedError,
  ErrorBoundaryHandler,
  globalErrorHandler,
  useErrorBoundary,
  withErrorHandling,
  handleValidationErrors,
  initializeErrorHandling,
  ERROR_TYPES,
  ERROR_SEVERITY
};

export default errorHandlingUtils;